import {
  CartCheckoutButton,
  CartCost,
  CartLineImage,
  CartLinePrice,
  CartLineProductTitle,
  CartLineQuantity,
  CartLineQuantityAdjustButton,
  CartLines,
  Link,
  Money,
  useCart,
  useCartLine,
} from '@shopify/hydrogen';
import {useEffect} from 'react';
import clsx from 'clsx';
import Button from '../elements/Button';
import {useCartStore} from '../../hooks/useCartStore';
import {AnimatePresence, motion} from 'framer-motion';
import translate from '../../utils/getTranslation';

export default function Cart() {
  const {isOpen} = useCartStore();
  const {lines, totalQuantity} = useCart();

  const TRANSITIONS = {
    DURATION: 0.4,
    EASE: [0.25, 0.1, 0.25, 1.0],
  };

  useEffect(() => {
    {
      isOpen
        ? document.documentElement.classList.add('overflow-hidden')
        : document.documentElement.classList.remove('overflow-hidden');
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.nav className="fixed inset-0 z-[90]">
          <CartBlur />
          <motion.div
            variants={{
              open: {
                x: 0,
                originY: 0,
                opacity: 1,
                transition: {
                  duration: TRANSITIONS.DURATION,
                  ease: TRANSITIONS.EASE,
                },
              },
              closed: {
                x: `100%`,
                transition: {
                  duration: TRANSITIONS.DURATION,
                  ease: TRANSITIONS.EASE,
                },
              },
            }}
            initial="closed"
            animate="open"
            exit="closed"
            className="absolute right-0 flex h-full w-full flex-col bg-neutral-100 text-neutral-900 md:w-1/2"
          >
            <CartHeader numLines={lines.length} />
            {totalQuantity === 0 ? (
              <CartEmpty />
            ) : (
              <>
                <CartItems />
                <CartFooter />
              </>
            )}
          </motion.div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
}

function CartBlur() {
  const {toggleOpen} = useCartStore();
  return (
    <motion.div
      onClick={toggleOpen}
      initial={{opacity: 0}}
      animate={{opacity: 0.2}}
      exit={{opacity: 0}}
      className={clsx('fixed inset-0 h-screen w-screen', 'bg-neutral-900')}
    ></motion.div>
  );
}

function CartHeader({numLines}: {numLines: number}) {
  const {toggleOpen} = useCartStore();
  return (
    <header
      className={clsx(
        'sticky top-0 flex items-center justify-between px-4 pt-5 md:px-8',
      )}
    >
      <div className="relative w-full items-center pb-4 text-sm">
        <div className="flex w-full justify-between gap-2">
          <p className="col-span-3">
            {translate('CART')}
            <span> ({numLines >= 0 && `${numLines}`})</span>
          </p>
          <button
            type="button"
            onClick={toggleOpen}
            className="justify-self-end"
          >
            {translate('CLOSE')}
          </button>
        </div>
      </div>
    </header>
  );
}

function CartItems() {
  const container = {
    hidden: {opacity: 0, y: -15},
    show: {
      opacity: 1,
      y: 0,
      transition: {
        delayChildren: 0.4,
        staggerChildren: 0.1,
        duration: 1,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
  };

  return (
    <motion.div
      role="table"
      aria-label="Shopping cart"
      className="flex-grow px-4 md:px-8"
      variants={container}
      initial="hidden"
      animate="show"
    >
      <CartLines>
        <LineInCart />
      </CartLines>
    </motion.div>
  );
}

function LineInCart() {
  const {merchandise, cost} = useCartLine();
  const cartLine = useCartLine();

  console.log(cartLine);

  const transitionItem = {
    hidden: {opacity: 0},
    show: {opacity: 1},
  };

  const firstVariant = merchandise.selectedOptions[0];
  const hasDefaultVariantOnly =
    firstVariant.name === 'Title' && firstVariant.value === 'Default Title';

  return (
    <motion.div
      variants={transitionItem}
      role="row"
      className="relative flex justify-between gap-4 border-b border-neutral-300 py-4"
    >
      {/* Image */}
      <Link to={`/products/${merchandise.product.handle}`}>
        <div role="cell" className="aspect-[4/5] w-[120px]">
          <CartLineImage
            className="h-full w-full bg-neutral-200 object-cover"
            loaderOptions={{width: 250, height: 250, crop: 'center'}}
          />
        </div>
      </Link>
      <div className="flex w-full flex-col justify-between gap-2">
        <div className="flex w-full items-start justify-between">
          {/* Title */}
          <div className="flex flex-col gap-2">
            <Link
              to={`/products/${merchandise.product.handle}`}
              className="max-w-md hover:text-neutral-600"
            >
              <CartLineProductTitle className="text-balance max-w-[75%]" />
            </Link>
            <div className="flex gap-2">
              {merchandise.priceV2.amount != cost.totalAmount.amount && (
                <Money
                  className="line-through opacity-40"
                  data={merchandise.priceV2}
                />
              )}
              <CartLinePrice />
            </div>
          </div>

          {/* Price */}
          <div className="flex items-end gap-2 justify-self-end text-right">
            {/* Remove */}
            <div role="cell">
              <CartLineQuantityAdjustButton
                adjust="remove"
                aria-label="Remove from cart"
                className="disabled:pointer-events-all text-sm disabled:cursor-wait"
              >
                <div className="flex aspect-square w-6 items-center justify-center rounded-sm leading-none transition-colors hover:bg-neutral-200">
                  ×
                </div>
              </CartLineQuantityAdjustButton>
            </div>
          </div>
        </div>

        <div className="flex w-full items-end justify-between">
          {/* Options */}
          <div className="gap-2 text-sm">
            {!hasDefaultVariantOnly &&
              merchandise.selectedOptions.map(({value, name}) => (
                <p key={value} className="mt-1">
                  <span className="text-neutral-500">{name} </span>
                  {value}
                </p>
              ))}
          </div>

          <div className="flex items-center justify-between gap-2 justify-self-end">
            {/* Quantity */}
            <div className="flex-shrink-0">
              <CartItemQuantity />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function CartItemQuantity() {
  return (
    <div className="flex items-center">
      <CartLineQuantityAdjustButton
        adjust="decrease"
        aria-label="Decrease quantity"
        className="disabled:pointer-events-all disabled:cursor-wait"
      >
        {/* <MinusCircleIcon /> */}
        <div className="flex aspect-square w-6 items-center justify-center rounded-sm leading-none transition-colors hover:bg-neutral-200">
          −
        </div>
      </CartLineQuantityAdjustButton>
      <CartLineQuantity
        as="div"
        className="min-w-[2rem] text-center leading-none"
      />
      <CartLineQuantityAdjustButton
        adjust="increase"
        aria-label="Increase quantity"
        className="disabled:pointer-events-all disabled:cursor-wait"
      >
        {/* <PlusCircleIcon /> */}
        <div className="flex aspect-square w-6 items-center justify-center rounded-sm leading-none transition-colors hover:bg-neutral-200">
          +
        </div>
      </CartLineQuantityAdjustButton>
    </div>
  );
}

function CartFooter() {
  return (
    <footer className="sticky bottom-0 grid gap-2 bg-neutral-100 p-6 text-neutral-900">
      <div>
        <div className="">
          <div
            role="table"
            aria-label="Cost summary"
            className="text-neutral-300"
          >
            <div
              className="flex justify-between border-t border-neutral-300 py-3 text-neutral-900"
              role="row"
            >
              <span className="" role="rowheader">
                {translate('SUBTOTAL')}
              </span>
              <CartCost
                amountType="subtotal"
                className="text-right text-md"
                role="cell"
              />
            </div>
          </div>

          <div className="w-full gap-2">
            <CartCheckoutButton
              className={clsx('w-full bg-neutral-800 p-3 text-neutral-100')}
            >
              {translate('CHECKOUT')}
            </CartCheckoutButton>
          </div>
          <p className="mt-4 text-center text-sm text-neutral-500">
            {translate('CHECKOUT_MESSAGE')}
          </p>
        </div>
      </div>
    </footer>
  );
}

function CartEmpty() {
  const {toggleOpen} = useCartStore();
  return (
    <div className="mt-16 flex flex-col gap-y-12 px-4 py-5 md:px-8">
      <h1 className="translate-y-[.15em] text-center font-kormelink text-3xl leading-none">
        {translate('EMPTY_CART')}
      </h1>
      <Button onClick={toggleOpen} type="button" className="text-neutral-900">
        {translate('CONTINUE_SHOPPING')}
      </Button>
    </div>
  );
}
